import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FilmInventory } from "@/types/inventory";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft } from "lucide-react";
import { FilmStatusBadge } from "@/components/inventory/FilmStatusBadge";
import { FilmActions } from "@/components/inventory/FilmActions";
import { toast } from "sonner";
import { format } from "date-fns";
import { useIsMobile } from "@/hooks/use-mobile";
import { useTranslation } from "@/hooks/useTranslation";

export default function FilmDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [film, setFilm] = useState<FilmInventory | null>(null);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const { t } = useTranslation();

  useEffect(() => {
    fetchFilm();
  }, [id]);

  const formatExpirationDate = (dateString: string | null) => {
    if (!dateString) return t('not_applicable');
    try {
      return format(new Date(dateString), 'MMMM yyyy');
    } catch {
      return t('invalid_date');
    }
  };

  const fetchFilm = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/auth');
        return;
      }

      const { data, error } = await supabase
        .from('film_inventory')
        .select('*')
        .eq('id', id)
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      if (!data) {
        navigate('/inventory');
        return;
      }

      setFilm(data as unknown as FilmInventory);
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('failed_load_film_details'));
      navigate('/inventory');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-6">
        <p>{t('loading')}</p>
      </div>
    );
  }

  if (!film) return null;

  return (
    <div className="container max-w-2xl py-6 px-4 space-y-6">
      <div className="flex items-center gap-2">
        <Button 
          variant="ghost" 
          size="icon"
          onClick={() => navigate('/inventory')}
          className="shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="text-2xl font-bold">{t('film_details')}</h1>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <CardTitle className="text-xl">
              {film.brand} {film.film_name}
            </CardTitle>
            <FilmStatusBadge status={film.status} className="shrink-0" />
          </div>
          <p className="text-sm text-muted-foreground">
            {t('last_updated')}: {format(new Date(film.created_at), 'MMMM d, yyyy')}
          </p>
        </CardHeader>
        <CardContent className="pt-4">
          <div className={`grid gap-6 ${isMobile ? 'grid-cols-2' : 'sm:grid-cols-2'}`}>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('type')}</p>
              <p className="font-medium">{film.film_type}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">ISO</p>
              <p className="font-medium">{film.iso}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('quantity')}</p>
              <p className="font-medium">{film.quantity}</p>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">{t('expiration_date')}</p>
              <p className="font-medium">
                {formatExpirationDate(film.expiration_date)}
              </p>
            </div>
          </div>

          {film.notes && (
            <div className="space-y-2 mt-6 pt-4 border-t">
              <p className="text-sm text-muted-foreground">{t('notes')}</p>
              <p className="p-4 bg-muted rounded-lg text-sm">{film.notes}</p>
            </div>
          )}
          
          <div className="flex justify-end mt-6 pt-4 border-t">
            <FilmActions 
              film={film} 
              showTransfer={true} 
              onActionComplete={fetchFilm}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
